/**
 * Attendance Data Schemas and Validation
 */

import { formatTime12hFromDb } from '../utils/timeDisplay';
import { formatDateDisplay } from '../utils/dateDisplay';

const renderDbClockTime = (value) => {
  if (value == null || value === '') return '--:--';
  const t = String(value).trim();
  if (t === '00:00' || t === '00:00:00') return '--:--';
  return formatTime12hFromDb(value);
};

// Attendance rectification schema
export const attendanceRectificationSchema = {
  intime: {
    required: true,
    pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
    message: 'Please select a valid in time'
  },
  outtime: {
    required: false,
    pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
    message: 'Please select a valid out time'
  },
  breaktime: {
    required: false,
    validate: (value) => {
      if (!value) return true;
      const num = parseInt(value);
      return !isNaN(num) && num >= 0 && num <= 480;
    },
    message: 'Break time must be between 0 and 480 minutes'
  },
  startDate: {
    required: true,
    type: 'date',
    validate: (value) => {
      const date = new Date(value);
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
      return date >= thirtyDaysAgo && date <= today;
    },
    message: 'Start date must be within the last 30 days'
  },
  endDate: {
    required: true,
    type: 'date',
    validate: (value, formData) => {
      const endDate = new Date(value);
      const startDate = new Date(formData.startDate);
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
      const diffInDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      return (
        endDate >= thirtyDaysAgo &&
        endDate <= today &&
        endDate >= startDate &&
        diffInDays <= 10
      );
    },
    message: 'End date must be within last 30 days, after start date, and range must be max 10 days'
  },
  reason: {
    required: true,
    minLength: 10,
    maxLength: 500,
    message: 'Reason must be 10-500 characters'
  }
};

// Manual attendance marking schema
export const attendanceMarkingSchema = {
  employeeId: {
    required: true,
    message: 'Employee ID is required'
  },
  date: {
    required: true,
    type: 'date',
    validate: (value) => new Date(value) <= new Date(),
    message: 'Date cannot be in the future'
  },
  inTime: {
    required: true,
    pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
    message: 'Please enter a valid in time (HH:MM)'
  },
  outTime: {
    required: false,
    pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
    message: 'Please enter a valid out time (HH:MM)'
  },
  breakTime: {
    required: false,
    pattern: /^([0-9]+):([0-5][0-9])$/,
    message: 'Please enter break time in HH:MM format'
  },
  status: {
    required: true,
    enum: ['Present', 'Absent', 'Half Day', 'Holiday', 'Leave'],
    message: 'Please select a valid status'
  },
  remarks: {
    required: false,
    maxLength: 200,
    message: 'Remarks must be less than 200 characters'
  }
};

// Mark absent schema
export const markAbsentSchema = {
  id: {
    required: true,
    type: 'number',
    message: 'Attendance record ID is required'
  },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  date: {
    required: true,
    type: 'date',
    message: 'Please select a date'
  },
  reason: {
    required: true,
    minLength: 10,
    maxLength: 500,
    message: 'Reason must be 10-500 characters'
  }
};

// Attendance status types
export const AttendanceStatus = {
  Present: { color: 'success', icon: '✅', label: 'Present' },
  Absent: { color: 'danger', icon: '❌', label: 'Absent' },
  'Half Day': { color: 'warning', icon: '⚠️', label: 'Half Day' },
  Holiday: { color: 'info', icon: '🏖️', label: 'Holiday' },
  Leave: { color: 'primary', icon: '📅', label: 'Leave' },
  'Paid Leave': { color: 'success', icon: '🏖️', label: 'Paid Leave' },
  'Work From Home': { color: 'purple', icon: '🏠', label: 'WFH' }
};

// Rectification request status
export const RectificationStatus = {
  pending: { color: 'warning', icon: '⏳', label: 'Pending' },
  approved: { color: 'success', icon: '✅', label: 'Approved' },
  rejected: { color: 'danger', icon: '❌', label: 'Rejected' }
};

// Default attendance data
export const defaultAttendanceData = {
  intime: '09:00',
  outtime: '18:00',
  breaktime: '60',
  startDate: '',
  endDate: '',
  reason: ''
};

// Attendance table columns
export const attendanceTableColumns = [
  {
    key: 'datestring',
    label: 'Date',
    sortable: true,
    render: (record) => formatDateDisplay(record.datestring)
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    filterable: true
  },
  {
    key: 'intime',
    label: 'In Time',
    sortable: true,
    render: (record) => renderDbClockTime(record.intime)
  },
  {
    key: 'outtime',
    label: 'Out Time',
    sortable: true,
    render: (record) => renderDbClockTime(record.outtime)
  },
  {
    key: 'worktime',
    label: 'Work Hours',
    sortable: true,
    render: (record) => record.worktime || '00:00:00'
  },
  {
    key: 'breaktime',
    label: 'Break Time',
    sortable: true,
    render: (record) => record.breaktime || '00:00:00'
  },
  {
    key: 'overtime',
    label: 'Overtime',
    sortable: true,
    render: (record) => record.overtime || '00:00:00'
  },
  {
    key: 'late_in',
    label: 'Late In',
    sortable: true,
    render: (record) => record.late_in || '00:00:00'
  },
  {
    key: 'erl_out',
    label: 'Early Out',
    sortable: true,
    render: (record) => record.erl_out || '00:00:00'
  }
];

// Rectification table columns
export const rectificationTableColumns = [
  {
    key: 'datestring',
    label: 'Date',
    sortable: true,
    render: (record) => formatDateDisplay(record.datestring)
  },
  {
    key: 'name',
    label: 'Employee',
    sortable: true,
    searchable: true
  },
  {
    key: 'intime',
    label: 'In Time',
    sortable: true,
    render: (record) => renderDbClockTime(record.intime)
  },
  {
    key: 'outtime',
    label: 'Out Time',
    sortable: true,
    render: (record) => renderDbClockTime(record.outtime)
  },
  {
    key: 'worktime',
    label: 'Work Hours',
    sortable: true
  },
  {
    key: 'reason',
    label: 'Reason',
    searchable: true,
    render: (record) => record.reason?.length > 50 ? 
      `${record.reason.substring(0, 50)}...` : record.reason
  },
  {
    key: 'request',
    label: 'Status',
    sortable: true,
    filterable: true
  }
];

// Time validation helpers
export const TimeValidation = {
  isValidTime: (timeString) => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeString);
  },
  
  isOutTimeAfterInTime: (inTime, outTime) => {
    if (!inTime || !outTime) return true;
    
    const inDate = new Date(`1970-01-01T${inTime}`);
    const outDate = new Date(`1970-01-01T${outTime}`);
    
    return outDate > inDate;
  },
  
  calculateWorkHours: (inTime, outTime, breakTime = '01:00') => {
    if (!inTime || !outTime) return '00:00:00';
    
    try {
      const inDate = new Date(`1970-01-01T${inTime}`);
      const outDate = new Date(`1970-01-01T${outTime}`);
      const [breakHours, breakMinutes] = breakTime.split(':').map(Number);
      
      const diffMs = outDate - inDate;
      const diffMinutes = Math.max(0, Math.floor(diffMs / 60000) - (breakHours * 60 + breakMinutes));
      
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    } catch (error) {
      return '00:00:00';
    }
  }
};

// Office hours configuration
export const OfficeHours = {
  startTime: '09:00',
  endTime: '18:00',
  lunchBreak: '01:00',
  graceTime: 15, // minutes
  halfDayHours: 4,
  fullDayHours: 8
};

export default {
  attendanceRectificationSchema,
  attendanceMarkingSchema,
  markAbsentSchema,
  AttendanceStatus,
  RectificationStatus,
  defaultAttendanceData,
  attendanceTableColumns,
  rectificationTableColumns,
  TimeValidation,
  OfficeHours
};
